// @ts-nocheck
import StoreAnchorMenu from "@store/Library/Components/Foundation/StoreAnchorMenu";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { TextFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface RowModel {
    fields: {
        linkTitle: string;
        anchor: string;
        isOpen: boolean;
    };
}

const enum fields {
    rows = "rows",
    isOpenByDefault = "isOpenByDefault",
    hideAnchorMenu = "hideAnchorMenu",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.rows]: RowModel[];
        [fields.isOpenByDefault]: boolean;
        [fields.hideAnchorMenu]: boolean;
    };
}

type Props = OwnProps;

export interface ProductDetailsAccordionStyles {
    anchorMenuWrapper?: InjectableCss;
    accordionWrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
}

export const productDetailsAccordionStyles: ProductDetailsAccordionStyles = {
    anchorMenuWrapper: {
        css: css`
            ${breakpointValues([pxToRem(30), null, pxToRem(40)], "margin-bottom")}
        `,
    },
    accordionWrapper: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    accordionSection: {
        titleTypographyProps: {
            forwardAs: "h2",
        },
    },
};

const styles = productDetailsAccordionStyles;

export const productDetailsAccordionWrapperId = "productDetailsAccordionWrapperId";

const ProductDetailsAccordion: FC<Props> = ({ id, fields: { rows, isOpenByDefault, hideAnchorMenu } }) => {
    const [accordionRows, setAccordionRows] = useState<RowModel[]>();

    useEffect(() => {
        const newRows: RowModel[] = rows.map(row => ({
            fields: {
                ...row.fields,
                isOpen: isOpenByDefault,
            },
        }));
        setAccordionRows(newRows);
    }, [rows]);

    const onToggleHandler = (expanded: boolean, anchor: string) => {
        const newRows: RowModel[] = rows.map(row => {
            if (row.fields.anchor === anchor) {
                return {
                    fields: {
                        ...row.fields,
                        isOpen: !expanded,
                    },
                };
            }

            return row;
        });
        setAccordionRows(newRows);
    };

    if (!accordionRows) {
        return null;
    }

    return (
        <>
            {!hideAnchorMenu && (
                <StyledWrapper {...styles.anchorMenuWrapper}>
                    <StoreAnchorMenu links={accordionRows} />
                </StyledWrapper>
            )}
            <StyledWrapper id={productDetailsAccordionWrapperId} {...styles.accordionWrapper}>
                <Accordion headingLevel={2}>
                    {accordionRows?.map(({ fields }, index) => {
                        const rowId = `${fields.anchor}-${index}`;
                        return (
                            <StyledWrapper key={fields.anchor}>
                                <AccordionSection
                                    title={fields.linkTitle}
                                    headerProps={{ id: fields.anchor, headingLevel: 2 }}
                                    uid={fields.anchor}
                                    onTogglePanel={expanded => onToggleHandler(expanded, fields.anchor)}
                                    expanded={fields.isOpen}
                                    {...styles.accordionSection}
                                >
                                    <Zone contentId={id} zoneName={`Row-${rowId}`} />
                                </AccordionSection>
                            </StyledWrapper>
                        );
                    })}
                </Accordion>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsAccordion,
    definition: {
        group: "Product Details",
        displayName: "Product Accordion",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.rows,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { linkTitle } }) => linkTitle,
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "anchor",
                        displayName: "Element ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTitle",
                        displayName: "Row Title",
                        editorTemplate: "TextField",
                        fieldType: "Translatable", // normaly not possible if the type is ChildFieldDefinition instead of FieldDefinition
                        defaultValue: "",
                        isRequired: true,
                    } as TextFieldDefinition,
                ],
            },
            {
                name: fields.isOpenByDefault,
                displayName: "Open all accordions sections by default",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.hideAnchorMenu,
                displayName: "Hide Anchor Menu",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
